import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ParallaxLayer from './ParallaxLayer';
import SettingsPanel from './SettingsPanel';
import useParallaxEffect from '../hooks/useParallaxEffect';
import '../styles/ParallaxScene.css';

// 画像をインポート（src内に画像を配置する場合）
import layer1Image from '../assets/images/layer1.png'; // 背景（最も遠い）
import layer2Image from '../assets/images/layer2.png';
import layer3Image from '../assets/images/layer3.png';
import layer4Image from '../assets/images/layer4.png';
import layer5Image from '../assets/images/layer5.png'; // 前景（最も近い）

const ParallaxScene = ({ 
  layerCount = 5, 
  initialSettings = {} 
}) => {
  // デフォルト設定
  const defaultSettings = {
    minDepth: -400,
    maxDepth: 0,
    minScale: 1.0,
    maxScale: 1.4,
    touchSensitivity: 0.05,
    gyroSensitivity: 2.0,
    decayRate: 0.95,
    maxTilt: 20
  };
  
  const [settings, setSettings] = useState({ ...defaultSettings, ...initialSettings });
  const [debugInfo, setDebugInfo] = useState({ x: 0, y: 0 });
  
  // パララックス効果のフック
  const { rotation, handlers } = useParallaxEffect({
    touchSensitivity: settings.touchSensitivity,
    gyroSensitivity: settings.gyroSensitivity,
    decayRate: settings.decayRate,
    maxTilt: settings.maxTilt
  });
  
  // レイヤーの設定を更新
  const handleSettingsApply = useCallback((newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, []);
  
  // レイヤー画像の配列 - メモ化する
  const layerImages = useMemo(() => [
    layer1Image, 
    layer2Image, 
    layer3Image, 
    layer4Image, 
    layer5Image
  ], []); // 空の依存配列 - インポートした画像は変更されないため
  
  // レイヤー計算 - パフォーマンスのためにメモ化
  const layers = useMemo(() => {
    const result = [];
    const { minDepth, maxDepth, minScale, maxScale } = settings;
    
    for (let i = 0; i < layerCount; i++) {
      const depthRatio = i / (layerCount - 1);
      const depth = minDepth + (maxDepth - minDepth) * depthRatio;
      const scale = maxScale - (maxScale - minScale) * depthRatio;
      
      // 対応する画像を取得
      const image = layerImages[i];
      
      result.push({
        index: i + 1,
        depth,
        scale: scale.toFixed(2),
        image,
        zIndex: layerCount - i
      });
    }
    
    return result;
  }, [layerCount, layerImages, settings]);
  
  // デバッグ情報の更新
  useEffect(() => {
    setDebugInfo({
      x: Math.round(rotation.y),
      y: Math.round(rotation.x)
    });
  }, [rotation]);

  return (
    <div 
      className="parallax-scene"
      {...handlers}
    >
      <div 
        className="parallax-container"
        style={{ 
          transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`
        }}
      >
        {layers.map(layer => (
          <ParallaxLayer
            key={layer.index}
            index={layer.index}
            depth={layer.depth}
            scale={layer.scale}
            image={layer.image}
            zIndex={layer.zIndex}
          />
        ))}
      </div>
      
      <div className="debug-indicator">
        X: {debugInfo.x}, Y: {debugInfo.y}
      </div>
      
      <SettingsPanel 
        settings={settings}
        onApply={handleSettingsApply}
      />
    </div>
  );
};

export default ParallaxScene;